import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ACTION_LOADING } from 'src/app/component/store/actions/actions';
import { FEATURES_EXCLUDE_SIDEBAR } from 'src/app/interface/features.interface';
import { RecordLog, RoundControl, ShiftControl } from 'src/app/interface/buildings.interface';
import { UserRoles } from 'src/app/interface/users.interface';
import { DataService } from 'src/app/services/data.service';
import { GeneralConfigService } from 'src/app/services/general-config.service';
import { ReservationsStackFeatureService } from 'src/app/services/reservations-stack-feature.service';
import { WhiteLabelService } from 'src/app/services/white-label.service';
import {
  FINANCIAL_SECTION_FEATURES,
  shouldShowAccountStatementsMenu,
  shouldShowMunilyPayMenuItem,
} from '../../constants/financial-menu-items.constants';
import { PROPRIETOR_ALLOWED_MAIN_FEATURES } from '../../constants/proprietor-access.constants';
import { applyReservationsStackPaths, mainFeatures, MenuSectionFeature, ROUTES_V2, SUB_MENU_ITEMS } from '../menu-items-v2';
import { SidebarMenuBuildResult } from '../models/sidebar-menu-build-result.model';
import { RouteInfo } from '../sidebar.metadata';

const ROUTES = ROUTES_V2;

enum MacroBuildingType {
  macrocommunity = 'macrocommunity',
  community = 'community',
}

interface MenuBuildContext {
  sidebarnavItems: RouteInfo[];
  bottomNavItems: RouteInfo[];
  contability: boolean;
  boardsRecordLog: RecordLog;
  boardsRoundControl: RoundControl;
  boardsShiftControl: ShiftControl;
}

@Injectable({
  providedIn: 'root',
})
export class SidebarMenuBuilderV2Service {
  private readonly HOME_MENU_PATHS = new Set([
    ROUTES[mainFeatures.dashboard].path,
    ROUTES[mainFeatures.dashboardMacro].path,
    ROUTES[mainFeatures.dashboardSuperAdmin].path,
  ]);

  constructor(
    private dataService: DataService,
    private generalConfigService: GeneralConfigService,
    private translateService: TranslateService,
    private whiteLabelService: WhiteLabelService,
    private reservationsStackFeature: ReservationsStackFeatureService,
  ) {}

  buildMenu(): Observable<SidebarMenuBuildResult> {
    // Paths already stamped by ReservationsStackFeatureService after the single probe.
    applyReservationsStackPaths(this.reservationsStackFeature.isV2);

    const ctx = this.createContext();
    const user = this.dataService.getUser();
    const building = this.dataService.getBuilding();
    const buildingCofig = building.config;
    const roles = user.roles ?? [''];
    const isMacro: boolean = !!user.buildings[0].config?.macrocommunity?.enable;
    const isAMacroChild: boolean =
      isMacro && user.buildings[0].config?.macrocommunity?.communities
        ? !this.isAMacroFather(user.buildings[0].config?.macrocommunity.communities)
        : false;

    this.applyBoardConfig(ctx, buildingCofig);

    const showFeatures = this.whiteLabelService.getMenuShowFeatures(building);
    const isCommitee = roles.indexOf(UserRoles.committee) > -1;

    if (user.superUser) {
      this.buildSuperAdminMenu(ctx);
      return of(this.toResult(ctx));
    }
    if (roles.indexOf('proprietor') > -1 && roles.indexOf('admin') === -1) {
      this.buildProprietorMenu(ctx);
      return of(this.toResult(ctx));
    }
    if (roles.indexOf('customized') > -1) {
      this.buildCustomizedMenu(ctx, user.features);
      return of(this.toResult(ctx));
    }
    if (showFeatures && showFeatures.length > 0) {
      this.buildWhiteLabelMenu(ctx, showFeatures, isMacro, isAMacroChild);
      return this.appendFinancialMenuItemsAndGroup(ctx, buildingCofig);
    }
    if (isCommitee) {
      this.buildCommitteeMenu(ctx);
      return of(this.toResult(ctx));
    }
    return this.buildStandardMenu(ctx, roles, isMacro, isAMacroChild, buildingCofig);
  }

  private createContext(): MenuBuildContext {
    return {
      sidebarnavItems: [],
      bottomNavItems: [],
      contability: false,
      boardsRecordLog: { enable: false, link: '' },
      boardsRoundControl: { enable: false },
      boardsShiftControl: { enable: false },
    };
  }

  private toResult(ctx: MenuBuildContext): SidebarMenuBuildResult {
    return {
      sidebarnavItems: ctx.sidebarnavItems,
      bottomNavItems: ctx.bottomNavItems,
    };
  }

  private applyBoardConfig(ctx: MenuBuildContext, buildingCofig: Record<string, unknown>): void {
    const recordLog = buildingCofig?.recordLog as RecordLog | undefined;
    const roundControl = buildingCofig?.roundControl as RoundControl | undefined;
    const shiftControl = buildingCofig?.shiftControl as ShiftControl | undefined;

    if (!recordLog && !roundControl && !shiftControl) {
      return;
    }

    if (shiftControl?.enable && shiftControl?.boardLink) {
      ctx.boardsShiftControl.enable = true;
      ctx.boardsShiftControl.boardLink = shiftControl.boardLink;
    }
    if (recordLog?.enable && recordLog?.boardLink) {
      ctx.boardsRecordLog.enable = true;
      ctx.boardsRecordLog.boardLink = recordLog.boardLink;
    }
    if (roundControl?.enable && roundControl?.boardLink) {
      ctx.boardsRoundControl.enable = true;
      ctx.boardsRoundControl.boardLink = roundControl.boardLink;
    }
  }

  private buildWhiteLabelMenu(
    ctx: MenuBuildContext,
    showFeatures: string[],
    isMacro: boolean,
    isAMacroChild: boolean,
  ): void {
    ctx.sidebarnavItems = [];
    showFeatures = this.withTicketsV2WhenPqrsShowFeature(showFeatures);

    const featureMapping: Record<string, string> = {
      notices: mainFeatures.notices,
      emergencies: mainFeatures.emergency,
      pqrs: mainFeatures.ticketsV2,
      ticketsV2: mainFeatures.ticketsV2,
      reservations: mainFeatures.reservations,
      visits: mainFeatures.visits,
      frequentAccess: mainFeatures.frequentAccess,
      users: mainFeatures.users,
      tenants: mainFeatures.users,
      registrations: mainFeatures.registrations,
      administration: mainFeatures.administration,
      areas: mainFeatures.areas,
      buildingsAdmin: mainFeatures.buildingsAdmin,
      noticesMacro: mainFeatures.noticesMacro,
      reservationsMacro: mainFeatures.reservationsMacro,
      usersMacro: mainFeatures.usersMacro,
      areasMacro: mainFeatures.areasMacro,
      administrativeDropdown: mainFeatures.administrativeDropdown,
      administrativeMacroDropdown: mainFeatures.administrativeMacroDropdown,
      accountStatements: mainFeatures.accountStatements,
    };

    const dropdownSubItems: Record<string, string[]> = {
      [mainFeatures.administrativeDropdown]: [
        mainFeatures.users,
        mainFeatures.registrations,
        mainFeatures.administration,
        mainFeatures.areas,
        mainFeatures.buildingsAdmin,
      ],
      [mainFeatures.administrativeMacroDropdown]: [mainFeatures.usersMacro, mainFeatures.areasMacro],
    };

    const mappedFeatures = showFeatures.map((feature) => featureMapping[feature] || feature).filter((item) => ROUTES[item]);
    const mainItems: string[] = [];
    const subItemsToInclude: Record<string, string[]> = {};

    mappedFeatures.forEach((feature) => {
      let isSubItem = false;
      for (const [dropdown, subItems] of Object.entries(dropdownSubItems)) {
        if (subItems.includes(feature)) {
          isSubItem = true;
          if (!subItemsToInclude[dropdown]) {
            subItemsToInclude[dropdown] = [];
          }
          subItemsToInclude[dropdown].push(feature);
          break;
        }
      }
      if (!isSubItem) {
        if (dropdownSubItems[feature]) {
          subItemsToInclude[feature] = [...dropdownSubItems[feature]];
        } else {
          mainItems.push(feature);
        }
      }
    });

    for (const [dropdown, subItems] of Object.entries(subItemsToInclude)) {
      if (ROUTES[dropdown] && subItems.length > 0) {
        const dropdownRoute = ROUTES[dropdown];
        const filteredSubmenu = subItems.map((subItem) => ROUTES[subItem]).filter(Boolean);
        if (filteredSubmenu.length > 0) {
          ctx.sidebarnavItems.push({
            ...dropdownRoute,
            submenu: this.sortListByTitle([...filteredSubmenu]),
          });
        }
      }
    }

    mainItems.forEach((item) => {
      if (ROUTES[item]) {
        ctx.sidebarnavItems.push(ROUTES[item]);
      }
    });

    ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
    this.adjacentManagementVisitsBeforeVisits(ctx.sidebarnavItems);

    if (isMacro && !isAMacroChild) {
      ctx.sidebarnavItems = ctx.sidebarnavItems.filter(
        (item) => item !== ROUTES[mainFeatures.knowledgeCenter] && item !== ROUTES[mainFeatures.dashboardMacro],
      );
    }
  }

  private adjacentManagementVisitsBeforeVisits(navItems: RouteInfo[]): void {
    const visitsRoute = ROUTES[mainFeatures.visits];
    const managementCandidates = [ROUTES[mainFeatures.managementVisitsMacro], ROUTES[mainFeatures.managementVisitsMacroBusiness]];

    let managementIdx = -1;
    let visitsIdx = -1;
    for (let i = 0; i < navItems.length; i++) {
      const item = navItems[i];
      if (managementIdx === -1 && managementCandidates.some((r) => r === item)) {
        managementIdx = i;
      }
      if (item === visitsRoute) {
        visitsIdx = i;
      }
    }
    if (managementIdx === -1 || visitsIdx === -1) {
      return;
    }
    const [managementItem] = navItems.splice(managementIdx, 1);
    const adjustedVisitsIdx = visitsIdx > managementIdx ? visitsIdx - 1 : visitsIdx;
    navItems.splice(adjustedVisitsIdx, 0, managementItem);
  }

  private buildProprietorMenu(ctx: MenuBuildContext): void {
    const items = PROPRIETOR_ALLOWED_MAIN_FEATURES.map((key) => {
      if (key === mainFeatures.administration) {
        return this.getProprietorAdministrativeItem();
      }
      if (key === mainFeatures.managementVisitsMacroBusiness) {
        const managementKey = this.dataService.getBuildingIsMacroFather()
          ? mainFeatures.managementVisitsMacro
          : mainFeatures.managementVisitsMacroBusiness;
        return ROUTES[managementKey];
      }
      return ROUTES[key];
    });
    ctx.sidebarnavItems = this.sortListByTitle(items);
    this.adjacentManagementVisitsBeforeVisits(ctx.sidebarnavItems);
    this.applyGrouping(ctx);
  }

  private getProprietorAdministrativeItem(): RouteInfo {
    return {
      ...ROUTES[mainFeatures.administrativeDropdown],
      title: ROUTES[mainFeatures.administrativeDropdown].title,
      submenu: [ROUTES[mainFeatures.users]],
    };
  }

  private buildSuperAdminMenu(ctx: MenuBuildContext): void {
    ctx.sidebarnavItems = [];
    ctx.sidebarnavItems.push(
      ROUTES[mainFeatures.buildings],
      ROUTES[mainFeatures.usersSuperAdmin],
      ROUTES[mainFeatures.services],
      ROUTES[mainFeatures.releases],
      ROUTES[mainFeatures.knowledgeCenter],
    );
    ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
    ctx.sidebarnavItems = this.groupMenuSections(ctx.sidebarnavItems, ROUTES[mainFeatures.dashboardSuperAdmin], ctx);
    ctx.sidebarnavItems.push(ROUTES[mainFeatures.maia], ROUTES[mainFeatures.legalPolicies]);
    ctx.sidebarnavItems = this.ensureHomeFirst(ctx.sidebarnavItems);
  }

  private buildCustomizedMenu(ctx: MenuBuildContext, features: Array<{ enable: boolean; feature: string[] }>): void {
    ctx.sidebarnavItems = [];
    this.builtCustomizedMenu(ctx, features);
    ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
    this.applyGrouping(ctx);
  }

  private builtCustomizedMenu(ctx: MenuBuildContext, features: Array<{ enable: boolean; feature: string[] }>): void {
    features.forEach((feature) => {
      if (!feature.enable) {
        return;
      }
      feature.feature.forEach((userFeature) => {
        const resolvedFeature = this.resolveV2MenuFeature(userFeature);
        if (FEATURES_EXCLUDE_SIDEBAR.indexOf(userFeature as typeof FEATURES_EXCLUDE_SIDEBAR[number]) < 0) {
          if (this.checkIsSubmenuItem(userFeature)) {
            const mainMenu = this.getMenuItem(userFeature);
            if (!ROUTES[mainMenu].submenu.includes(ROUTES[resolvedFeature])) {
              ROUTES[mainMenu].submenu.push(ROUTES[resolvedFeature]);
            }
            if (ctx.sidebarnavItems.indexOf(ROUTES[mainMenu]) < 0) {
              ctx.sidebarnavItems.push(ROUTES[mainMenu]);
            }
          } else if (ctx.sidebarnavItems.indexOf(ROUTES[resolvedFeature]) < 0) {
            ctx.sidebarnavItems.push(ROUTES[resolvedFeature]);
          }
        }
      });

      if (ctx.sidebarnavItems.includes(ROUTES[mainFeatures.visits])) {
        const typeRouter = this.dataService.getBuildingIsMacroFather() ? mainFeatures.visitsMacro : mainFeatures.visitsMacroBusiness;
        const index = ctx.sidebarnavItems.indexOf(ROUTES[mainFeatures.visits]);
        ctx.sidebarnavItems[index] = ROUTES[typeRouter];
      }

      if (ctx.sidebarnavItems.includes(ROUTES[mainFeatures.managementVisitsMacroBusiness])) {
        const typeRouter = this.dataService.getBuildingIsMacroFather()
          ? mainFeatures.managementVisitsMacro
          : mainFeatures.managementVisitsMacroBusiness;
        const index = ctx.sidebarnavItems.indexOf(ROUTES[mainFeatures.managementVisitsMacro]);
        ctx.sidebarnavItems[index] = ROUTES[typeRouter];
      }
    });
  }

  private buildStandardMenu(
    ctx: MenuBuildContext,
    roles: string[],
    isMacro: boolean,
    isAMacroChild: boolean,
    buildingCofig: Record<string, unknown>,
    excludedMainItems?: string[],
  ): Observable<SidebarMenuBuildResult> {
    if (roles.indexOf('admin') > -1 && (!isMacro || isAMacroChild)) {
      return this.buildTraditionalAdminMenu(ctx, buildingCofig, excludedMainItems);
    }
    if (roles.indexOf('admin') > -1 && isMacro && !isAMacroChild) {
      this.buildMacroAdminMenu(ctx, excludedMainItems);
      return of(this.toResult(ctx));
    }
    return of(this.toResult(ctx));
  }

  private buildTraditionalAdminMenu(
    ctx: MenuBuildContext,
    buildingCofig: Record<string, unknown>,
    excludedMainItems?: string[],
  ): Observable<SidebarMenuBuildResult> {
    this.dataService.isLoading({ action: ACTION_LOADING, payload: true });
    return this.generalConfigService.getGeneralConfigFeatures().pipe(
      map((config: { data: { feature: { contability: boolean } } }) => {
        ctx.sidebarnavItems = [];

        if (buildingCofig.chatV2 && (buildingCofig.chatV2 as { enable: boolean }).enable) {
          ctx.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.chats]);
        }
        if (config.data.feature && config.data.feature.contability) {
          ctx.contability = true;
        }
        this.appendFinancialMenuItems(ctx, buildingCofig);
        if (buildingCofig.pqrs && (buildingCofig.pqrs as { enable: boolean }).enable) {
          ctx.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.ticketsV2]);
        }

        const accessControl = buildingCofig.accessControl as {
          preRegisterVisit?: { enable: boolean };
          vigilantVisit?: { enable: boolean };
        };
        if (accessControl?.preRegisterVisit?.enable || accessControl?.vigilantVisit?.enable) {
          ctx.sidebarnavItems.push(ROUTES[mainFeatures.controlAccess]);
        }

        const directory = buildingCofig.directory as { enable?: boolean } | undefined;
        if (directory?.enable || (buildingCofig.invitation as { directCallVigilant?: boolean })?.directCallVigilant) {
          ctx.sidebarnavItems.push(ROUTES[mainFeatures.vigilantCalls]);
        }

        const munilyPh = buildingCofig.munilyPh as { enable?: boolean; link?: string } | undefined;
        if (munilyPh?.enable) {
          if (munilyPh.link) {
            ROUTES[mainFeatures.munilyProviders].path = munilyPh.link;
          }
          ctx.sidebarnavItems.push(ROUTES[mainFeatures.munilyProviders]);
        }

        if (ctx.boardsRecordLog.enable || ctx.boardsRoundControl.enable || ctx.boardsShiftControl.enable) {
          const boards = ROUTES[mainFeatures.boards];
          boards.submenu = [];
          if (ctx.boardsShiftControl.enable) {
            boards.submenu.push(ROUTES[mainFeatures.shiftControl]);
          }
          if (ctx.boardsRecordLog.enable) {
            boards.submenu.push(ROUTES[mainFeatures.recordLog]);
          }
          if (ctx.boardsRoundControl.enable) {
            boards.submenu.push(ROUTES[mainFeatures.roundControl]);
          }
          ctx.sidebarnavItems.push(ROUTES[mainFeatures.boards]);
        }

        ctx.sidebarnavItems.push(
          ROUTES[mainFeatures.notices],
          ROUTES[mainFeatures.reservations],
          ROUTES[mainFeatures.visits],
          ROUTES[mainFeatures.frequentAccess],
          ROUTES[mainFeatures.administrativeDropdown],
          ROUTES[mainFeatures.knowledgeCenter],
          ROUTES[mainFeatures.emergency],
        );
        ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
        this.filterMenuItemsByKeys(ctx, excludedMainItems);
        ctx.sidebarnavItems = this.groupMenuSections(ctx.sidebarnavItems, ROUTES[mainFeatures.dashboard], ctx);
        this.dataService.isLoading({ action: ACTION_LOADING, payload: false });
        return this.toResult(ctx);
      }),
      catchError(() => {
        this.dataService.isLoading({ action: ACTION_LOADING, payload: false });
        return of(this.toResult(ctx));
      }),
    );
  }

  /**
   * Ítems financieros independientes (OR). La sección Financiera aparece si al menos uno queda tras agrupar.
   * - Estados de cuenta: `building.config.accountState.enable` o `generalConfig.feature.contability`
   * - Munily Pay: `building.config.fintech.enable` + acceso a ruta legacy
   */
  private appendFinancialMenuItems(ctx: MenuBuildContext, buildingCofig: Record<string, unknown>): void {
    const accountState = buildingCofig.accountState as { enable?: boolean } | undefined;
    if (
      shouldShowAccountStatementsMenu(ctx.contability, accountState) &&
      !this.hasMenuItemByFeature(ctx, mainFeatures.accountStatements)
    ) {
      ctx.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.accountStatements]);
    }
    const fintech = buildingCofig.fintech as { enable?: boolean } | undefined;
    if (
      shouldShowMunilyPayMenuItem(this.dataService.getUser(), fintech) &&
      !this.hasMenuItemByFeature(ctx, mainFeatures.munilyPayTransactions)
    ) {
      ctx.sidebarnavItems.push(ROUTES[mainFeatures.munilyPayTransactions]);
    }
  }

  /** White label: añade financieros (config del PH) y agrupa secciones. */
  private appendFinancialMenuItemsAndGroup(
    ctx: MenuBuildContext,
    buildingCofig: Record<string, unknown>,
  ): Observable<SidebarMenuBuildResult> {
    const accountState = buildingCofig.accountState as { enable?: boolean } | undefined;
    if (accountState?.enable) {
      this.appendFinancialMenuItems(ctx, buildingCofig);
      this.applyGrouping(ctx);
      return of(this.toResult(ctx));
    }

    return this.generalConfigService.getGeneralConfigFeatures().pipe(
      map((config: { data: { feature: { contability: boolean } } }) => {
        if (config.data.feature?.contability) {
          ctx.contability = true;
        }
        this.appendFinancialMenuItems(ctx, buildingCofig);
        this.applyGrouping(ctx);
        return this.toResult(ctx);
      }),
      catchError(() => {
        this.appendFinancialMenuItems(ctx, buildingCofig);
        this.applyGrouping(ctx);
        return of(this.toResult(ctx));
      }),
    );
  }

  private hasMenuItemByFeature(ctx: MenuBuildContext, feature: string): boolean {
    const target = ROUTES[feature];
    if (!target?.path) {
      return false;
    }
    const matches = (item: RouteInfo): boolean => item === target || item.path === target.path;
    return ctx.sidebarnavItems.some((item) => matches(item) || (item.submenu ?? []).some(matches));
  }

  private buildMacroAdminMenu(ctx: MenuBuildContext, excludedMainItems?: string[]): void {
    ctx.sidebarnavItems = [];
    ctx.sidebarnavItems.push(
      ROUTES[mainFeatures.noticesMacro],
      ROUTES[mainFeatures.reservationsMacro],
      ROUTES[mainFeatures.administrativeMacroDropdown],
      ROUTES[mainFeatures.knowledgeCenter],
    );
    ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
    this.filterMenuItemsByKeys(ctx, excludedMainItems);
    ctx.sidebarnavItems = this.groupMenuSections(ctx.sidebarnavItems, ROUTES[mainFeatures.dashboardMacro], ctx);
  }

  private buildCommitteeMenu(ctx: MenuBuildContext): void {
    ctx.sidebarnavItems = [ROUTES[mainFeatures.committeeDashboard]];
    ctx.sidebarnavItems = this.sortListByTitle(ctx.sidebarnavItems);
  }

  private applyGrouping(ctx: MenuBuildContext): void {
    ctx.sidebarnavItems = this.groupMenuSections(ctx.sidebarnavItems, undefined, ctx);
  }

  private groupMenuSections(items: RouteInfo[], homeItem: RouteInfo | undefined, ctx: MenuBuildContext): RouteInfo[] {
    const flattenedItems = this.flattenLegacyDropdowns(items);
    const consumedItems = new Set<RouteInfo>();
    const groupedMenu: RouteInfo[] = [];

    if (homeItem) {
      groupedMenu.push(homeItem);
      consumedItems.add(homeItem);
    }

    this.getMenuSections().forEach((section) => {
      const submenu = section.items
        .map((feature) => this.takeMenuItemByFeature(flattenedItems, consumedItems, feature))
        .filter(Boolean) as RouteInfo[];

      if (submenu.length > 0) {
        groupedMenu.push({
          ...ROUTES[section.dropdown],
          submenu,
        });
      }
    });

    flattenedItems.forEach((item) => {
      if (!consumedItems.has(item) && item !== homeItem) {
        groupedMenu.push(item);
      }
    });

    // Orden predefinido de secciones/ítems (getMenuSections); solo Inicio va primero.
    return this.extractBottomNavItems(this.ensureHomeFirst(groupedMenu), ctx);
  }

  private extractBottomNavItems(menu: RouteInfo[], ctx: MenuBuildContext): RouteInfo[] {
    const bottomFeatures = this.getBottomMenuFeatures();
    ctx.bottomNavItems = [];

    return menu.filter((item) => {
      if (bottomFeatures.includes(this.getMenuItemFeature(item))) {
        ctx.bottomNavItems.push(item);
        return false;
      }
      return true;
    });
  }

  private flattenLegacyDropdowns(items: RouteInfo[]): RouteInfo[] {
    const legacyDropdownTitles = [
      ROUTES[mainFeatures.administrativeDropdown].title,
      ROUTES[mainFeatures.administrativeMacroDropdown].title,
      ROUTES[mainFeatures.doorOpening].title,
      ROUTES[mainFeatures.controlAccess].title,
      ROUTES[mainFeatures.boards].title,
    ];

    return items.reduce<RouteInfo[]>((result, item) => {
      if (item?.submenu?.length > 0 && legacyDropdownTitles.includes(item.title)) {
        return [...result, ...item.submenu];
      }
      return [...result, item];
    }, []);
  }

  private getMenuSections(): { dropdown: MenuSectionFeature; items: string[] }[] {
    return [
      {
        dropdown: MenuSectionFeature.operationalDropdown,
        items: [
          mainFeatures.ticketsV2,
          mainFeatures.notices,
          mainFeatures.noticesMacro,
          mainFeatures.chats,
          mainFeatures.reservations,
          mainFeatures.reservationsMacro,
        ],
      },
      {
        dropdown: MenuSectionFeature.financialDropdown,
        items: [...FINANCIAL_SECTION_FEATURES],
      },
      {
        dropdown: MenuSectionFeature.securityDropdown,
        items: [
          mainFeatures.visits,
          mainFeatures.visitsMacro,
          mainFeatures.visitsMacroBusiness,
          mainFeatures.frequentAccess,
          mainFeatures.roundControl,
          mainFeatures.shiftControl,
          mainFeatures.emergency,
          mainFeatures.vigilantCalls,
          mainFeatures.managementVisitsMacro,
          mainFeatures.managementVisitsMacroBusiness,
          mainFeatures.recordLog,
          mainFeatures.accessManagement,
          mainFeatures.securityCenters,
          mainFeatures.doorOpeningAdmin,
          mainFeatures.door,
          mainFeatures.accessControlMacroAdmin,
          mainFeatures.accessControlMacroBusiness,
          mainFeatures.securityCenterMacroAdmin,
          mainFeatures.securityCenterMacroBusiness,
        ],
      },
      {
        dropdown: MenuSectionFeature.configurationsDropdown,
        items: [
          mainFeatures.administration,
          mainFeatures.users,
          mainFeatures.usersMacro,
          mainFeatures.usersSuperAdmin,
          mainFeatures.registrations,
          mainFeatures.areas,
          mainFeatures.areasMacro,
          mainFeatures.buildingsAdmin,
          mainFeatures.buildings,
          mainFeatures.munilyProviders,
        ],
      },
    ];
  }

  private getBottomMenuFeatures(): string[] {
    return [mainFeatures.knowledgeCenter];
  }

  private takeMenuItemByFeature(items: RouteInfo[], consumedItems: Set<RouteInfo>, feature: string): RouteInfo | undefined {
    const menuItem = items.find((item) => !consumedItems.has(item) && this.getMenuItemFeature(item) === feature);
    if (menuItem) {
      consumedItems.add(menuItem);
    }
    return menuItem;
  }

  private getMenuItemFeature(menuItem: RouteInfo): string | undefined {
    const features = Object.values(mainFeatures) as string[];

    const byReference = features.find((feature) => ROUTES[feature] === menuItem);
    if (byReference) {
      return byReference;
    }

    if (menuItem?.path) {
      const byPath = features.find((feature) => ROUTES[feature]?.path === menuItem.path);
      if (byPath) {
        return byPath;
      }
    }

    return features.find((feature) => {
      const route = ROUTES[feature];
      return route?.path === menuItem?.path && route?.title === menuItem?.title;
    });
  }

  private filterMenuItemsByKeys(ctx: MenuBuildContext, excludedMainItems?: string[]): void {
    if (!excludedMainItems || excludedMainItems.length === 0) {
      return;
    }
    ctx.sidebarnavItems = ctx.sidebarnavItems.filter((menuItem) => {
      const menuItemKey = Object.keys(mainFeatures).find((key) => ROUTES[mainFeatures[key]] === menuItem);
      return !menuItemKey || !excludedMainItems.includes(menuItemKey);
    });
  }

  private checkIsSubmenuItem(userFeature: string): boolean {
    return !!SUB_MENU_ITEMS.find((element) => element.submenu === userFeature);
  }

  private getMenuItem(userFeature: string): string {
    return SUB_MENU_ITEMS.find((element) => element.submenu === userFeature)?.main;
  }

  private isHomeMenuItem(item: { path?: string }): boolean {
    return !!item?.path && this.HOME_MENU_PATHS.has(item.path);
  }

  private ensureHomeFirst(list: RouteInfo[]): RouteInfo[] {
    const homeItems = list.filter((item) => this.isHomeMenuItem(item));
    if (!homeItems.length) {
      return list;
    }
    const otherItems = list.filter((item) => !this.isHomeMenuItem(item));
    return [...homeItems, ...otherItems];
  }

  private withTicketsV2WhenPqrsShowFeature(showFeatures: string[]): string[] {
    if (!showFeatures.includes('pqrs') || showFeatures.includes('ticketsV2')) {
      return showFeatures;
    }
    return [...showFeatures, 'ticketsV2'];
  }

  /** En v2, PQRS legacy se sustituye por Tickets (`/app/tickets`). */
  private resolveV2MenuFeature(featureKey: string): string {
    if (featureKey === mainFeatures.pqrs || featureKey === 'pqrs') {
      return mainFeatures.ticketsV2;
    }
    return featureKey;
  }

  private sortListByTitle(list: RouteInfo[]): RouteInfo[] {
    const sorted = list.sort((a, b) => {
      if (a.submenu?.length > 0) {
        a.submenu = this.sortListByTitle(a.submenu);
      }
      const titleA = this.translateService.instant(a.title).toLowerCase();
      const titleB = this.translateService.instant(b.title).toLowerCase();
      return titleA.localeCompare(titleB, 'es', { sensitivity: 'base' });
    });
    return this.ensureHomeFirst(sorted);
  }

  private isAMacroFather(communities: Array<{ type: string }>): boolean {
    return communities.findIndex((community) => community.type === MacroBuildingType.macrocommunity) > -1;
  }
}
